"use client"
import { useState, useEffect, useCallback } from "react"
import { 
  ChevronLeft, 
  ChevronRight,
  Database, 
  BarChart3,
  TrendingDown,
  AlertTriangle,
  Loader2,
  Home,
  Clock,
  Rows3,
  Target,
  Lightbulb,
  CheckCircle2,
  Ghost,
  Users
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { auth } from "../components/Authenticator/firebase"
import { API_URL } from "../config/api"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const KPISemillero = () => {
  const navigate = useNavigate()
  
  // Estados
  const [analisisList, setAnalisisList] = useState([])
  const [selectedAnalisisId, setSelectedAnalisisId] = useState(null)
  const [analisisData, setAnalisisData] = useState(null)
  const [resultados, setResultados] = useState(null)
  
  const [loadingList, setLoadingList] = useState(true)
  const [executing, setExecuting] = useState(false)
  const [error, setError] = useState(null)
  const [isMobile, setIsMobile] = useState(false)

  // Detectar móvil
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Cargar lista de análisis
  const loadAnalisisList = useCallback(async () => {
    try {
      setLoadingList(true)
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const response = await fetch(`${API_URL}/api/analisis/lista`, {
        headers: { Authorization: `Bearer ${token}` }
      })

      if (!response.ok) throw new Error('Error al cargar análisis')

      const data = await response.json()
      if (data.success) {
        setAnalisisList(data.analisis)
        if (data.analisis.length > 0 && !selectedAnalisisId) {
          setSelectedAnalisisId(data.analisis[0].id)
        }
      }
    } catch (err) {
      console.error('Error cargando lista:', err)
      setError(err.message)
    } finally {
      setLoadingList(false)
    }
  }, [selectedAnalisisId])

  useEffect(() => {
    loadAnalisisList()
  }, [loadAnalisisList])

  // Ejecutar análisis
  const ejecutarAnalisis = useCallback(async (id) => {
    try {
      setExecuting(true)
      setError(null)
      setResultados(null)
      
      const token = await auth.currentUser?.getIdToken()
      if (!token) throw new Error('No autenticado')

      const response = await fetch(`${API_URL}/api/analisis/${id}/ejecutar`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const data = await response.json()
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.sqlError || 'Error al ejecutar')
      }
      
      setAnalisisData(data.analisis)
      setResultados(data.resultados)
      
    } catch (err) {
      console.error('Error ejecutando análisis:', err)
      setError(err.message)
    } finally {
      setExecuting(false)
    }
  }, [])

  // Cuando cambia el análisis seleccionado
  useEffect(() => {
    if (selectedAnalisisId) {
      ejecutarAnalisis(selectedAnalisisId)
    }
  }, [selectedAnalisisId, ejecutarAnalisis])

  // Navegación
  const currentIndex = analisisList.findIndex(a => a.id === selectedAnalisisId)
  
  const goToNext = () => {
    if (currentIndex < analisisList.length - 1) {
      setSelectedAnalisisId(analisisList[currentIndex + 1].id)
    }
  }

  const goToPrev = () => {
    if (currentIndex > 0) {
      setSelectedAnalisisId(analisisList[currentIndex - 1].id)
    }
  }

  // Formatear valor para tabla
  const formatValue = (value, columnName) => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'number') {
      if (columnName.toLowerCase().includes('porcentaje') || columnName.toLowerCase().includes('caida')) {
        return `${value.toFixed(1)}%`
      }
      return value.toLocaleString('es-ES')
    }
    return String(value)
  }

  // Obtener estilo de riesgo/estado
  const getRiesgoStyle = (value) => {
    const str = String(value).toUpperCase()
    // Análisis #1 - Riesgos
    if (str.includes('RIESGO ALTO')) return { color: '#dc2626', backgroundColor: '#fef2f2', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('RIESGO MEDIO')) return { color: '#d97706', backgroundColor: '#fffbeb', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('RIESGO BAJO')) return { color: '#059669', backgroundColor: '#ecfdf5', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    // Análisis #2 - Prioridades (100% Perdidos vs Reducidos)
    if (str.includes('CRITICO')) return { color: '#dc2626', backgroundColor: '#fef2f2', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('PERDIDO') && !str.includes('100%')) return { color: '#ea580c', backgroundColor: '#fff7ed', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('SEVERA')) return { color: '#d97706', backgroundColor: '#fffbeb', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('MODERADA')) return { color: '#059669', backgroundColor: '#ecfdf5', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    // Análisis #2 - Grupos
    if (str.includes('100% PERDIDO')) return { color: '#dc2626', backgroundColor: '#fef2f2', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('REDUCIDO')) return { color: '#f59e0b', backgroundColor: '#fffbeb', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    // Análisis #3 - Estados Fantasma
    if (str.includes('PERDIDO')) return { color: '#dc2626', backgroundColor: '#fef2f2', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('PUEDE APARECER')) return { color: '#059669', backgroundColor: '#ecfdf5', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('SIN PATRON')) return { color: '#d97706', backgroundColor: '#fffbeb', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    // Tendencias
    if (str.includes('CRECIENTE')) return { color: '#059669', backgroundColor: '#ecfdf5', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('DECRECIENTE')) return { color: '#dc2626', backgroundColor: '#fef2f2', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    if (str.includes('ESTABLE')) return { color: '#6b7280', backgroundColor: '#f3f4f6', padding: '4px 10px', borderRadius: '6px', fontWeight: 600, fontSize: '11px' }
    return {}
  }

  // Preparar datos para gráfico - dinámico según análisis
  const prepareChartData = () => {
    if (!resultados || !resultados.filas || !analisisData) return []
    
    if (analisisData.id === 1) {
      return resultados.filas.slice(0, 15).map(fila => ({
        name: fila.nombre_cliente?.substring(0, 20) || 'N/A',
        value: Number(fila.bandejas_perdidas) || 0,
        estado: fila.clasificacion_riesgo
      }))
    } else if (analisisData.id === 2) {
      // Análisis #2 - 100% Perdidos vs Reducidos
      return resultados.filas.slice(0, 15).map(fila => ({
        name: fila.nombre_cliente?.substring(0, 20) || 'N/A',
        value: Number(fila.volumen_perdido) || 0,
        estado: fila.prioridad,
        grupo: fila.grupo
      }))
    } else if (analisisData.id === 3) {
      // Análisis #3 - Clientes Fantasma
      return resultados.filas.slice(0, 15).map(fila => ({
        name: fila.nombre_cliente?.substring(0, 20) || 'N/A',
        value: Number(fila.promedio_historico) || 0,
        estado: fila.estado_cliente
      }))
    }
    return []
  }

  const chartData = prepareChartData()

  // Colores del gráfico según estado
  const getBarColor = (estado, grupo) => {
    const str = String(estado || '').toUpperCase()
    // Análisis #1
    if (str.includes('ALTO')) return '#dc2626'
    if (str.includes('MEDIO')) return '#f59e0b'
    if (str.includes('BAJO')) return '#10b981'
    // Análisis #2 - por grupo y prioridad
    if (str.includes('CRITICO')) return '#dc2626'
    if (str.includes('PERDIDO') && String(grupo || '').includes('PERDIDO')) return '#ea580c'
    if (str.includes('SEVERA')) return '#f59e0b'
    if (str.includes('MODERADA')) return '#10b981'
    // Análisis #3 - Fantasma
    if (str.includes('PERDIDO')) return '#dc2626'
    if (str.includes('SIN PATR')) return '#f59e0b'
    if (str.includes('PUEDE APARECER')) return '#10b981'
    return '#6b7280'
  }
  
  // Título del gráfico según análisis
  const getChartTitle = () => {
    if (!analisisData) return 'Gráfico'
    if (analisisData.id === 1) return 'Top 15 Clientes con Mayor Caída'
    if (analisisData.id === 2) return 'Top 15 Clientes por Volumen Perdido'
    if (analisisData.id === 3) return 'Top 15 Clientes Fantasma por Volumen Histórico'
    return 'Top 15 Clientes'
  }
  
  // Label del tooltip según análisis
  const getTooltipLabel = () => {
    if (!analisisData) return 'Valor'
    if (analisisData.id === 1) return 'Pérdida'
    if (analisisData.id === 2) return 'Volumen perdido'
    if (analisisData.id === 3) return 'Promedio histórico'
    return 'Valor'
  }

  // Configuración de iconos y colores por análisis
  const getAnalisisStyle = (id) => {
    const styles = {
      1: { icon: TrendingDown, color: '#dc2626', bgGradient: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)' },
      2: { icon: Users, color: '#ea580c', bgGradient: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)' },
      3: { icon: Ghost, color: '#8b5cf6', bgGradient: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)' },
      // Agregar más estilos para análisis 4-20
    }
    return styles[id] || styles[1]
  }

  const analisisStyle = analisisData ? getAnalisisStyle(analisisData.id) : getAnalisisStyle(1)
  const AnalisisIcon = analisisStyle.icon

  // Resumen de resultados - dinámico según análisis
  const getResumen = () => {
    if (!resultados || !resultados.filas || !analisisData) return null
    const filas = resultados.filas
    
    if (analisisData.id === 1) {
      // Análisis #1 - Caída de volumen
      const totalPerdidas = filas.reduce((acc, f) => acc + (Number(f.bandejas_perdidas) || 0), 0)
      const riesgoAlto = filas.filter(f => String(f.clasificacion_riesgo || '').toUpperCase().includes('ALTO')).length
      const riesgoMedio = filas.filter(f => String(f.clasificacion_riesgo || '').toUpperCase().includes('MEDIO')).length
      const riesgoBajo = filas.filter(f => String(f.clasificacion_riesgo || '').toUpperCase().includes('BAJO')).length
      return { 
        tipo: 'riesgo',
        totalPerdidas, 
        riesgoAlto, 
        riesgoMedio, 
        riesgoBajo, 
        totalClientes: filas.length 
      }
    } else if (analisisData.id === 2) {
      // Análisis #2 - 100% Perdidos vs Reducidos
      const totalPerdido = filas.reduce((acc, f) => acc + (Number(f.volumen_perdido) || 0), 0)
      const grupo1 = filas.filter(f => String(f.grupo || '').includes('PERDIDO')).length
      const grupo2 = filas.filter(f => String(f.grupo || '').includes('REDUCIDO')).length
      const criticos = filas.filter(f => String(f.prioridad || '').toUpperCase().includes('CRITICO')).length
      const severos = filas.filter(f => String(f.prioridad || '').toUpperCase().includes('SEVERA')).length
      return {
        tipo: 'grupos',
        totalPerdido,
        grupo1,
        grupo2,
        criticos,
        severos,
        totalClientes: filas.length
      }
    } else if (analisisData.id === 3) {
      // Análisis #3 - Clientes fantasma
      const totalPromedio = filas.reduce((acc, f) => acc + (Number(f.promedio_historico) || 0), 0)
      const perdidos = filas.filter(f => String(f.estado_cliente || '').toUpperCase().includes('PERDIDO')).length
      const puedeAparecer = filas.filter(f => String(f.estado_cliente || '').toUpperCase().includes('PUEDE APARECER')).length
      const sinPatron = filas.filter(f => String(f.estado_cliente || '').toUpperCase().includes('SIN PATR')).length
      return {
        tipo: 'fantasma',
        totalPromedio,
        perdidos,
        puedeAparecer,
        sinPatron,
        totalClientes: filas.length
      }
    }
    
    return { tipo: 'generico', totalClientes: filas.length }
  }

  const resumen = getResumen()

  return (
    <div style={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#fbf9f4',
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      fontFamily: '"Plus Jakarta Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      zIndex: 100
    }}>
      <div style={{
        padding: isMobile ? '16px' : '24px',
        paddingBottom: '60px',
        minHeight: '100%',
        boxSizing: 'border-box'
      }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: isMobile ? '8px 10px' : '8px 14px',
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#374151'
            }}
          >
            <Home size={16} />
            {!isMobile && <span>Inicio</span>}
          </button>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: isMobile ? '8px 12px' : '8px 16px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            borderRadius: '10px',
            color: 'white'
          }}>
            <Database size={isMobile ? 16 : 18} />
            <span style={{ fontWeight: 600, fontSize: isMobile ? '13px' : '15px' }}>KPI Semillero</span>
          </div>
        </div>

        {/* Navegación entre análisis */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'white',
          padding: '4px',
          borderRadius: '10px',
          border: '1px solid #e2e8f0'
        }}>
          <button 
            onClick={goToPrev} 
            disabled={currentIndex <= 0 || executing}
            style={{
              padding: isMobile ? '8px' : '8px 12px',
              border: 'none',
              borderRadius: '6px',
              background: currentIndex <= 0 ? '#f3f4f6' : '#f9fafb',
              cursor: currentIndex <= 0 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              color: currentIndex <= 0 ? '#9ca3af' : '#374151',
              fontWeight: 500
            }}
          >
            <ChevronLeft size={16} />
            {!isMobile && 'Anterior'}
          </button>
          
          <div style={{
            padding: isMobile ? '8px 10px' : '8px 16px',
            fontSize: '12px',
            fontWeight: 600,
            color: '#10b981',
            background: '#f0fdf4',
            borderRadius: '6px',
            whiteSpace: 'nowrap'
          }}>
            {currentIndex + 1} / {analisisList.length}
          </div>
          
          <button 
            onClick={goToNext}
            disabled={currentIndex >= analisisList.length - 1 || executing}
            style={{
              padding: isMobile ? '8px' : '8px 12px',
              border: 'none',
              borderRadius: '6px',
              background: currentIndex >= analisisList.length - 1 ? '#f3f4f6' : '#f9fafb',
              cursor: currentIndex >= analisisList.length - 1 ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '12px',
              color: currentIndex >= analisisList.length - 1 ? '#9ca3af' : '#374151',
              fontWeight: 500
            }}
          >
            {!isMobile && 'Siguiente'}
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Estado de carga inicial */}
      {loadingList && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px',
          color: '#6b7280'
        }}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginRight: '12px' }} />
          <span>Cargando análisis...</span>
        </div>
      )}

      {/* Contenido del análisis */}
      {!loadingList && analisisData && (
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* 1. TÍTULO */}
          <section style={{
            background: 'white',
            borderRadius: '16px',
            padding: isMobile ? '16px' : '24px',
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? '12px' : '16px' }}>
              <div style={{
                width: isMobile ? '44px' : '52px',
                height: isMobile ? '44px' : '52px',
                borderRadius: '12px',
                background: analisisStyle.bgGradient,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <AnalisisIcon size={isMobile ? 20 : 24} color={analisisStyle.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                  flexWrap: 'wrap'
                }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#10b981',
                    background: '#f0fdf4',
                    padding: '3px 8px',
                    borderRadius: '5px'
                  }}>
                    #{analisisData.id}
                  </span>
                  <span style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    background: '#f3f4f6',
                    padding: '3px 8px',
                    borderRadius: '5px'
                  }}>
                    {analisisData.categoria}
                  </span>
                  {resultados && (
                    <span style={{
                      fontSize: '11px',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}>
                      <Clock size={11} />
                      {resultados.tiempoEjecucion}ms
                    </span>
                  )}
                </div>
                <h1 style={{
                  fontSize: isMobile ? '17px' : '22px',
                  fontWeight: 700,
                  color: '#111827',
                  margin: 0,
                  lineHeight: 1.3,
                  wordBreak: 'break-word'
                }}>
                  {analisisData.titulo}
                </h1>
              </div>
            </div>
          </section>

          {/* 2. INFORMACIÓN - QUÉ HACE */}
          {analisisData.explicacion && (
            <section style={{
              background: 'white',
              borderRadius: '16px',
              padding: isMobile ? '16px' : '24px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px'
              }}>
                <Target size={18} color="#10b981" />
                <h2 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 600, color: '#111827', margin: 0 }}>
                  ¿Qué hace este análisis?
                </h2>
              </div>

              <p style={{
                fontSize: isMobile ? '13px' : '14px',
                color: '#374151',
                lineHeight: 1.6,
                marginBottom: '20px',
                padding: isMobile ? '12px' : '16px',
                background: '#f0fdf4',
                borderRadius: '10px',
                borderLeft: '4px solid #10b981'
              }}>
                {analisisData.explicacion.objetivo}
              </p>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))',
                gap: '10px'
              }}>
                {analisisData.explicacion.pasos?.map((paso) => (
                  <div key={paso.numero} style={{
                    display: 'flex',
                    gap: '12px',
                    padding: '14px',
                    background: '#f9fafb',
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '8px',
                      background: '#10b981',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      fontWeight: 700,
                      flexShrink: 0
                    }}>
                      {paso.numero}
                    </div>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px' }}>
                        {paso.titulo}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: 1.5 }}>
                        {paso.descripcion}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Estado de ejecución */}
          {executing && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              padding: '40px',
              background: 'white',
              borderRadius: '16px',
              marginBottom: '20px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <Loader2 size={28} color="#10b981" style={{ animation: 'spin 1s linear infinite' }} />
              <div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#111827' }}>Ejecutando análisis...</div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>Consultando base de datos</div>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '20px',
              background: '#fef2f2',
              borderRadius: '12px',
              marginBottom: '20px',
              border: '1px solid #fecaca'
            }}>
              <AlertTriangle size={20} color="#dc2626" />
              <div>
                <div style={{ fontWeight: 600, color: '#991b1b', marginBottom: '4px' }}>Error al ejecutar</div>
                <div style={{ fontSize: '13px', color: '#b91c1c' }}>{error}</div>
              </div>
            </div>
          )}

          {/* 3. GRÁFICO */}
          {resultados && chartData.length > 0 && (
            <section style={{
              background: 'white',
              borderRadius: '16px',
              padding: isMobile ? '16px' : '24px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px'
              }}>
                <BarChart3 size={18} color="#8b5cf6" />
                <h2 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 600, color: '#111827', margin: 0 }}>
                  {getChartTitle()}
                </h2>
              </div>

              <div style={{ width: '100%', height: isMobile ? 280 : 380 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
                    <XAxis 
                      type="number" 
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      tickFormatter={(v) => v.toLocaleString()}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      width={isMobile ? 80 : 150}
                      tick={{ fontSize: 11, fill: '#374151' }}
                    />
                    <Tooltip 
                      formatter={(value) => [value.toLocaleString() + ' bandejas', getTooltipLabel()]}
                      contentStyle={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={getBarColor(entry.estado, entry.grupo)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '20px',
                marginTop: '16px',
                flexWrap: 'wrap'
              }}>
                {analisisData?.id === 1 ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#dc2626' }} />
                      Riesgo Alto
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#f59e0b' }} />
                      Riesgo Medio
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#10b981' }} />
                      Riesgo Bajo
                    </div>
                  </>
                ) : analisisData?.id === 2 ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#dc2626' }} />
                      Crítico / 100% Perdido
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#ea580c' }} />
                      Perdido (hace 2+ años)
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#f59e0b' }} />
                      Reducción Severa
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#10b981' }} />
                      Reducción Moderada
                    </div>
                  </>
                ) : analisisData?.id === 3 ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#dc2626' }} />
                      Perdido
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#f59e0b' }} />
                      Sin patrón
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6b7280' }}>
                      <div style={{ width: 12, height: 12, borderRadius: 3, background: '#10b981' }} />
                      Puede aparecer
                    </div>
                  </>
                ) : null}
              </div>
            </section>
          )}

          {/* 4. TABLA */}
          {resultados && resultados.filas.length > 0 && (
            <section style={{
              background: 'white',
              borderRadius: '16px',
              padding: isMobile ? '12px' : '24px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '12px',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Rows3 size={18} color="#3b82f6" />
                  <h2 style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 600, color: '#111827', margin: 0 }}>
                    Datos Detallados
                  </h2>
                </div>
                <span style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  background: '#f3f4f6',
                  padding: '4px 10px',
                  borderRadius: '6px'
                }}>
                  {resultados.totalFilas} registros
                </span>
              </div>

              <div style={{
                overflowX: 'auto',
                borderRadius: '10px',
                border: '1px solid #e5e7eb',
                WebkitOverflowScrolling: 'touch',
                maxWidth: '100%'
              }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: isMobile ? '11px' : '13px',
                  minWidth: '700px'
                }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      {resultados.columnas.map((col, i) => (
                        <th key={i} style={{
                          padding: '12px 14px',
                          textAlign: 'left',
                          fontWeight: 600,
                          color: '#374151',
                          borderBottom: '1px solid #e5e7eb',
                          whiteSpace: 'nowrap'
                        }}>
                          {col.replace(/_/g, ' ')}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.filas.map((fila, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                        {resultados.columnas.map((col, j) => (
                          <td key={j} style={{
                            padding: '11px 14px',
                            borderBottom: '1px solid #f3f4f6',
                            color: '#4b5563',
                            whiteSpace: 'nowrap'
                          }}>
                            {col.toLowerCase().includes('riesgo') ? (
                              <span style={getRiesgoStyle(fila[col])}>
                                {fila[col]}
                              </span>
                            ) : (
                              formatValue(fila[col], col)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* 5. RESUMEN FINAL */}
          {resumen && analisisData.explicacion && (
            <section style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '16px',
              padding: isMobile ? '16px' : '24px',
              marginBottom: '16px',
              border: '1px solid #fbbf24'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: '16px'
              }}>
                <Lightbulb size={20} color="#92400e" />
                <h2 style={{ fontSize: isMobile ? '15px' : '17px', fontWeight: 700, color: '#78350f', margin: 0 }}>
                  Resumen e Insights
                </h2>
              </div>

              {/* KPIs del resumen - dinámicos según análisis */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: isMobile ? '8px' : '12px',
                marginBottom: '20px'
              }}>
                {resumen.tipo === 'riesgo' && (
                  <>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#92400e', fontWeight: 600, marginBottom: '4px' }}>TOTAL PERDIDAS</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#78350f' }}>{resumen.totalPerdidas.toLocaleString()}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>bandejas</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600, marginBottom: '4px' }}>🔴 RIESGO ALTO</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#dc2626' }}>{resumen.riesgoAlto}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>clientes</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#d97706', fontWeight: 600, marginBottom: '4px' }}>🟡 RIESGO MEDIO</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#d97706' }}>{resumen.riesgoMedio}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>clientes</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#059669', fontWeight: 600, marginBottom: '4px' }}>🟢 RIESGO BAJO</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#059669' }}>{resumen.riesgoBajo}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>clientes</div>
                    </div>
                  </>
                )}
                {resumen.tipo === 'grupos' && (
                  <>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#92400e', fontWeight: 600, marginBottom: '4px' }}>VOLUMEN PERDIDO</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#78350f' }}>{resumen.totalPerdido.toLocaleString()}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>bandejas</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600, marginBottom: '4px' }}>🔴 100% PERDIDOS</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#dc2626' }}>{resumen.grupo1}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>clientes</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#f59e0b', fontWeight: 600, marginBottom: '4px' }}>🟡 REDUCIDOS</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#f59e0b' }}>{resumen.grupo2}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>clientes</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600, marginBottom: '4px' }}>⚠️ CRÍTICOS</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#dc2626' }}>{resumen.criticos}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>acción urgente</div>
                    </div>
                  </>
                )}
                {resumen.tipo === 'fantasma' && (
                  <>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#92400e', fontWeight: 600, marginBottom: '4px' }}>VOLUMEN EN RIESGO</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#78350f' }}>{resumen.totalPromedio.toLocaleString()}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>bandejas/año</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#dc2626', fontWeight: 600, marginBottom: '4px' }}>❌ PERDIDOS</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#dc2626' }}>{resumen.perdidos}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>ya pasó su fecha</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#059669', fontWeight: 600, marginBottom: '4px' }}>⏳ PUEDEN APARECER</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#059669' }}>{resumen.puedeAparecer}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>aún no es su mes</div>
                    </div>
                    <div style={{ background: 'white', borderRadius: '10px', padding: isMobile ? '12px' : '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '10px', color: '#d97706', fontWeight: 600, marginBottom: '4px' }}>⚠️ SIN PATRÓN</div>
                      <div style={{ fontSize: isMobile ? '18px' : '22px', fontWeight: 700, color: '#d97706' }}>{resumen.sinPatron}</div>
                      <div style={{ fontSize: '10px', color: '#a16207' }}>requiere análisis</div>
                    </div>
                  </>
                )}
              </div>

              {/* Decisiones */}
              <div style={{
                background: 'rgba(255,255,255,0.7)',
                borderRadius: '10px',
                padding: isMobile ? '12px' : '16px',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: isMobile ? '12px' : '13px', fontWeight: 600, color: '#78350f', marginBottom: '10px' }}>
                  ¿Qué decisiones permite tomar?
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {analisisData.explicacion.decisiones?.map((dec, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '6px',
                      fontSize: isMobile ? '12px' : '13px',
                      color: '#92400e'
                    }}>
                      <CheckCircle2 size={14} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                      <span>{dec}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Insight CEO */}
              {analisisData.explicacion.insightCEO && (
                <div style={{
                  background: 'rgba(255,255,255,0.85)',
                  borderRadius: '10px',
                  padding: isMobile ? '12px' : '16px',
                  borderLeft: '4px solid #f59e0b'
                }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                    💡 INSIGHT PARA EL CEO
                  </div>
                  <p style={{
                    fontSize: isMobile ? '12px' : '13px',
                    color: '#78350f',
                    lineHeight: 1.5,
                    margin: 0,
                    fontStyle: 'italic'
                  }}>
                    "{analisisData.explicacion.insightCEO}"
                  </p>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* CSS para animación */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      </div>
    </div>
  )
}

export default KPISemillero
